<template>
  <!-- Informations générales -->
  <div class="flex gap-4 px-4 pb-8 relative">
    <div
      v-if="perm().projects != 'modification'"
      style="position:absolute;top:0;left:0;width:100%;height:100%;z-index:1000;cursor:not-allowed;background:rgba(255,255,255,0.01);"
      @mousedown.stop
      @mouseup.stop
      @click.stop
      @pointerdown.stop
      @pointerup.stop
      @touchstart.stop
      @touchend.stop
    ></div>

    <!-- Div info -->
    <div class="flex-grow flex flex-col w-2/5 gap-8 h-fit">
      <div class="w-full text-left">
        <div class="font-semibold text-left mb-1">Client</div>
        <SelectCombobox
          v-model="project.clientId"
          :options="clientOptions"
          label=""
          placeholder="Sélectionner un client"
          :allow-empty="true"
          :empty-label="'Sélectionner un client'"
          :display-value="clientDisplay"
          :showAddOption="true"
          addOptionLabel="Ajouter un client"
          @select="onClientSelected"
          @add-new="popupClientVisible = true"
        />
      </div>

      <!-- Type de projet -->
      <div class="flex flex-col items-center w-full text-left gap-1">
        <div class="font-semibold w-full">Type de projet</div>
        <InputCombobox
          v-model="project.type"
          :options="typeOptions"
          placeholder="Créer un type de projet"
          :allow-empty="true"
        />
      </div>

      <!-- Statut -->
      <div class="flex flex-col items-center w-full text-left gap-1">
        <div class="font-semibold w-full">Statut</div>
        <select class="basicInput w-full" v-model="project.status">
          <option value="En cours">En cours</option>
          <option value="Terminé">Terminé</option>
          <option value="Annulé">Annulé</option>
          <option value="Devis à faire">Devis à faire</option>
          <option value="En attente">En attente</option>
          <option value="En pause">En pause</option>
          <option value="A programmer">A programmer</option>
          <option value="A attribuer">A attribuer</option>
        </select>
      </div>

      <!-- Date du projet -->
      <div class="w-full flex gap-2">
        <div class="flex flex-col items-center w-1/2 text-left gap-1">
          <div class="font-semibold w-full">Du</div>
          <input class="basicInput w-full" 
            type="date" 
            v-model="project.startDate" 
            @keydown.prevent   
            @paste.prevent
            @wheel.prevent
          />
        </div>
        <div class="flex flex-col items-center w-1/2 text-left gap-1">
          <div class="font-semibold w-full">Au</div>
          <input class="basicInput w-full" 
            type="date" 
            v-model="project.endDate" 
            @keydown.prevent   
            @paste.prevent
            @wheel.prevent
          />
        </div>
      </div>

      <!-- Adresse du projet -->
      <div class="flex flex-col gap-4">
        <div class="flex flex-col w-full">
          <div class="font-semibold text-left mb-1">Adresse</div>
          <input class="basicInput w-full" v-model="project.address"/>
          <!-- <div class="text-left truncate h-6" v-else>{{ project.address }}</div> -->
        </div>
        <div class="flex gap-2">
          <div class="flex flex-col w-4/5">
            <div class="font-semibold text-left mb-1">Ville</div>
            <input class="basicInput w-full" v-model="project.city"/>
          </div>
          <div class="flex flex-col w-1/5">
            <div class="font-semibold text-left mb-1">CP</div>
            <input class="basicInput w-full" v-model="project.postalCode"/>
          </div>
        </div>
      </div>
    </div>

    <div class="border border-gray-300"></div>

    <!-- Div description -->
    <div class="flex flex-col w-3/5 gap-8">
      <div class="w-full text-left">
        <div class="font-semibold text-left mb-1">Responsable</div>
        <SelectCombobox
          v-model="project.managerId"
          :options="managerOptions"
          label=""
          placeholder="Sélectionner une ressource"
          :allow-empty="true"
          :empty-label="'Sélectionner une ressource'"
          :display-value="managerDisplay"
        />
      </div>
      <div class="w-full text-left">
        <div class="font-semibold text-left mb-1">Co-Responsable</div>
        <SelectCombobox
          v-model="project.coManagerId"
          :options="managerOptions"
          label=""
          placeholder="Sélectionner une ressource"
          :allow-empty="true"
          :empty-label="'Sélectionner une ressource'"
          :display-value="managerDisplay"
        />
      </div>
      <div class="flex flex-col w-full flex-grow">
        <div class="font-semibold text-left mb-1">Description/Notes</div>
        <textarea class="basicInput px-1 flex-grow resize-none" v-model="project.description"></textarea>
      </div>
    </div>
  </div>

  <PopupClient class="z-10"
    :visible="popupClientVisible" 
    @close="popupClientVisible = false" 
    @add="addClient"
  />

</template>

<script>
import ClientInfo from './FurtherInfo/ClientInfo.vue';
import InputSelect from '@/components/Others/InputSelect.vue';
import SelectCombobox from '@/components/Others/SelectCombobox.vue';
import InputCombobox from '@/components/Others/InputCombobox.vue';
import PopupClient from '@/components/Popups/PopupClient.vue';

import { addClient } from '@/services/clientService';

import { fetchProjects } from '@/services/projectService';
import { fetchClients } from '@/services/clientService';
import { fetchRessources } from '@/services/ressourceService';

import { inject } from 'vue';

export default {
  name: 'GeneralInfo',
  components: {
    ClientInfo,
    InputSelect,
    SelectCombobox,
    PopupClient,
    InputCombobox,
  },
  props: ['project', 'isEditing'],
  data() {
    return {
      projects: [],
      clients: [],
      ressources: [],
      popupClientVisible: false,
    }
  },
  setup() {
    const perm = inject('userPerm');
    return { perm };
  },
  computed: {
    typeOptions() {
      // Récupère tous les types uniques
      let projectTypes = Array.from(new Set(this.projects.map(project => project.type)));
      // Retire les types vides
      projectTypes = projectTypes.filter(type => type !== '');
      return projectTypes;
    },
    clientOptions() {
      return this.clients.map(client => ({
        value: client.id,
        label: client.contacts[0].lastName + ' ' + client.contacts[0].firstName
      }));
    },
    clientDisplay() {
      return (id) => {
        const client = this.clients.find(c => c.id === id);
        return client ? client.contacts[0].lastName + ' ' + client.contacts[0].firstName : '';
      }
    },
    managerOptions() {
      return this.ressources.map(ressource => ({
        value: ressource.id,
        label: ressource.lastName + ' ' + ressource.firstName
      }));
    },
    managerDisplay() {
      return (id) => {
        const ressource = this.ressources.find(r => r.id === id);
        return ressource ? ressource.lastName + ' ' + ressource.firstName : '';
      }
    },
  },
  methods: {
    // Formate un objet date au format jj/mm/aaaa
    formatDate(date) {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0'); // Les mois commencent à 0
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
    onClientSelected(clientId) {
      this.project.clientId = clientId;
    },
    async addClient(client) {
      if (!client) return;
      const newClient = await addClient(client);
      this.clients.push(newClient);
      this.project.clientId = newClient.id;
    },
  },
  async mounted(){
    this.projects = await fetchProjects();
    this.clients = await fetchClients();
    this.ressources = await fetchRessources();
    this.ressources.sort((a, b) => a.lastName.localeCompare(b.lastName));
    this.project.status = this.project.status || 'En cours'; // Définit un statut par défaut si non défini
  },
  watch: {
    'project.startDate'(newStart) {
      if (this.project.endDate && this.project.endDate < newStart) {
        this.project.endDate = newStart;
      }
    },
    'project.endDate'(newEnd) {
      if (newEnd && newEnd < this.project.startDate) {
        this.project.endDate = this.project.startDate;
      }
    }
  },
}

</script>